import { Team, Song, GameState } from '../types';

const STORAGE_KEYS = {
  TEAMS: 'hitster-teams',
  PLAYLIST: 'hitster-playlist',
  GAME_STATE: 'hitster-game-state',
  LIVE_STATE: 'hitster-live-state',
};

// Use API when available, static mode only for file:// or explicit setting
const isStaticMode = typeof window !== 'undefined' && window.location.protocol === 'file:';
const API_BASE = process.env.REACT_APP_API_URL || '';

// Helper to check if localStorage is available (might be blocked in private mode)
const isLocalStorageAvailable = (): boolean => {
  try {
    const testKey = '__test__';
    localStorage.setItem(testKey, testKey);
    localStorage.removeItem(testKey);
    return true;
  } catch (e) {
    return false;
  }
};

// ============================================
// SERVER SYNC FUNCTIONS (disabled in static mode)
// ============================================

// Save playlist to server (async, fire-and-forget for localStorage sync)
export const syncPlaylistToServer = async (playlist: Song[]): Promise<void> => {
  if (isStaticMode) return; // Skip in static mode
  try {
    const playlistToSave = playlist.map(song => ({
      ...song,
      audioUrl: (song.audioUrl || '').startsWith('blob:') ? '' : (song.audioUrl || ''),
    }));
    
    await fetch(`${API_BASE}/api/playlist`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ playlist: playlistToSave }),
    });
    console.log('📋 Playlist synced to server');
  } catch (error) {
    console.warn('Could not sync playlist to server:', error);
  }
};

// Load playlist from server OR static JSON file
export const loadPlaylistFromServer = async (): Promise<Song[] | null> => {
  try {
    // In static mode, load from static JSON file in the same folder
    const url = isStaticMode ? './data/playlist.json' : `${API_BASE}/api/playlist`;
    const response = await fetch(url);
    const data = await response.json();
    
    // Handle both formats: direct array or {playlist: [...]}
    const playlist = Array.isArray(data) ? data : data.playlist;
    
    if (playlist && playlist.length > 0) {
      console.log(`📋 Loaded ${playlist.length} songs from ${isStaticMode ? 'static file' : 'server'}`);
      return playlist.map((song: Song) => ({
        ...song,
        questions: song.questions || [],
      }));
    }
    return null;
  } catch (error) {
    console.warn('Could not load playlist:', error);
    return null;
  }
};

// Save teams to server (disabled in static mode)
export const syncTeamsToServer = async (teams: Team[]): Promise<void> => {
  if (isStaticMode) return; // Skip in static mode
  try {
    await fetch(`${API_BASE}/api/teams`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ teams }),
    });
    console.log('👥 Teams synced to server');
  } catch (error) {
    console.warn('Could not sync teams to server:', error);
  }
};

// Load teams from server (in static mode, only use localStorage)
export const loadTeamsFromServer = async (): Promise<Team[] | null> => {
  if (isStaticMode) return null; // Use localStorage only in static mode
  try {
    const response = await fetch(`${API_BASE}/api/teams`);
    const data = await response.json();
    if (data.teams && data.teams.length > 0) {
      console.log(`👥 Loaded ${data.teams.length} teams from server`);
      return data.teams;
    }
    return null;
  } catch (error) {
    console.warn('Could not load teams from server:', error);
    return null;
  }
};

// ============================================
// LOCAL STORAGE FUNCTIONS (with server sync)
// ============================================

export const saveTeams = (teams: Team[]): void => {
  if (isLocalStorageAvailable()) {
    try {
      localStorage.setItem(STORAGE_KEYS.TEAMS, JSON.stringify(teams));
    } catch (error) {
      console.warn('Could not save teams to localStorage:', error);
    }
  }
  // Also sync to server (fire-and-forget)
  syncTeamsToServer(teams);
};

export const loadTeams = (): Team[] | null => {
  if (!isLocalStorageAvailable()) return null;
  try {
    const data = localStorage.getItem(STORAGE_KEYS.TEAMS);
    if (!data) return null;
    const teams = JSON.parse(data) as Team[];
    // Validate that it's an array
    if (!Array.isArray(teams)) return null;
    return teams;
  } catch (error) {
    console.warn('Could not load teams from localStorage:', error);
    return null;
  }
};

export const savePlaylist = (playlist: Song[]): void => {
  // Save playlist including audio URLs (server URLs from YouTube downloads are persistent)
  // Only skip blob: URLs as they are temporary
  if (isLocalStorageAvailable()) {
    try {
      const playlistToSave = playlist.map(song => ({
        ...song,
        audioUrl: (song.audioUrl || '').startsWith('blob:') ? '' : (song.audioUrl || ''),
      }));
      localStorage.setItem(STORAGE_KEYS.PLAYLIST, JSON.stringify(playlistToSave));
    } catch (error) {
      console.warn('Could not save playlist to localStorage:', error);
    }
  }
  // Also sync to server (fire-and-forget)
  syncPlaylistToServer(playlist);
};

export const loadPlaylist = (): Song[] | null => {
  if (!isLocalStorageAvailable()) return null;
  try {
    const data = localStorage.getItem(STORAGE_KEYS.PLAYLIST);
    if (!data) return null;
    
    const playlist = JSON.parse(data) as Song[];
    // Validate that it's an array
    if (!Array.isArray(playlist)) return null;
    // Ensure questions array exists for backwards compatibility
    return playlist.map(song => ({
      ...song,
      audioUrl: song.audioUrl || '',
      questions: song.questions || [],
    }));
  } catch (error) {
    console.warn('Could not load playlist from localStorage:', error);
    return null;
  }
};

export const saveGameState = (state: Partial<GameState>): void => {
  if (isLocalStorageAvailable()) {
    try {
      localStorage.setItem(STORAGE_KEYS.GAME_STATE, JSON.stringify(state));
    } catch (error) {
      console.warn('Could not save game state to localStorage:', error);
    }
  }
};

export const loadGameState = (): Partial<GameState> | null => {
  if (!isLocalStorageAvailable()) return null;
  try {
    const data = localStorage.getItem(STORAGE_KEYS.GAME_STATE);
    if (!data) return null;
    return JSON.parse(data);
  } catch (error) {
    console.warn('Could not load game state from localStorage:', error);
    return null;
  }
};

// Live state for cross-window synchronization
export interface LiveState {
  isPlaying: boolean;
  isPaused: boolean;
  showResults: boolean;
  roundNumber: number;
  currentSong: Song | null;
  timestamp: number;
}

export const saveLiveState = (state: LiveState): void => {
  if (isLocalStorageAvailable()) {
    try {
      localStorage.setItem(STORAGE_KEYS.LIVE_STATE, JSON.stringify(state));
    } catch (error) {
      console.warn('Could not save live state to localStorage:', error);
    }
  }
};

export const loadLiveState = (): LiveState | null => {
  if (!isLocalStorageAvailable()) return null;
  try {
    const data = localStorage.getItem(STORAGE_KEYS.LIVE_STATE);
    if (!data) return null;
    return JSON.parse(data);
  } catch (error) {
    console.warn('Could not load live state from localStorage:', error);
    return null;
  }
};

export const LIVE_STATE_KEY = STORAGE_KEYS.LIVE_STATE;

export const clearStorage = (): void => {
  if (!isLocalStorageAvailable()) return;
  try {
    Object.values(STORAGE_KEYS).forEach(key => {
      localStorage.removeItem(key);
    });
  } catch (error) {
    console.warn('Could not clear localStorage:', error);
  }
};
